import { Feature } from 'geojson';
import { round } from 'lodash';
import { useMemo } from 'react';

import ColorSwatches from '@/components/colorSwatches';
import Select from '@/components/select';
import SingleSlider from '@/components/slider/singleSlider';
import Typography from '@/components/typography';

import { fontTypesMap, fontWeightMap } from '../../config';
import {
  CustomizeColorSwatchEnum,
  customizeSwatchConfig,
} from '../settingsConfig';
import { NewValues } from '.';

const textColorSwatchConfig = { ...customizeSwatchConfig };

delete textColorSwatchConfig[CustomizeColorSwatchEnum.NONE];

export const textSizeOptions = [
  { label: 'Small', value: 14 },
  { label: 'Medium', value: 20 },
  { label: 'Large', value: 26 },
];

interface IProps {
  activeFeature: Feature | null;
  handleValueChange: (newValues: NewValues) => void;
}
const TextSettings = ({ activeFeature, handleValueChange }: IProps) => {
  const activeColorConfig = Object.values(customizeSwatchConfig).find(
    (val) => val.color === activeFeature?.properties?.color
  );

  const activeBorderColorConfig = Object.values(customizeSwatchConfig).find(
    (val) => val.color === activeFeature?.properties?.['border-color']
  );

  const activeOpacityConfig = useMemo(
    () => activeFeature?.properties?.opacity,
    [activeFeature?.properties?.opacity]
  );

  const activeBorderOpacityConfig = useMemo(
    () => activeFeature?.properties?.['border-opacity'],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeFeature?.properties?.['border-opacity']]
  );

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col gap-2">
          <Typography className="text-gray-70" variant="body2">
            Text Size
          </Typography>
          <Select
            additionalProps={{ hideDefault: true }}
            className="w-full"
            options={textSizeOptions}
            placeholder="Size"
            value={activeFeature?.properties?.size}
            onChange={(e) => handleValueChange({ size: e })}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Typography className="text-gray-70" variant="body2">
            Text Weight
          </Typography>
          <Select
            additionalProps={{ hideDefault: true }}
            className="w-full"
            options={Object.values(fontWeightMap).map((fontWeight) => {
              return {
                label: fontWeight.label,
                value: fontWeight.id,
              };
            })}
            placeholder="Weight"
            value={activeFeature?.properties?.['font-weight']}
            onChange={(e) => handleValueChange({ 'font-weight': e })}
          />
        </div>
      </div>
      <ColorSwatches
        active={activeColorConfig}
        label="Text Color"
        swatchConfig={textColorSwatchConfig}
        onClick={(e) => handleValueChange({ color: e.color })}
      />
      <div className="flex flex-col gap-2">
        <Typography className="text-gray-70" variant="body2">
          Opacity
        </Typography>
        <SingleSlider
          defaultValue={round(activeOpacityConfig * 100)}
          handleChange={(val) => {
            handleValueChange({ opacity: val / 100 });
          }}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Typography className="text-gray-70" variant="body2">
          Font Family
        </Typography>
        <Select
          additionalProps={{ hideDefault: true }}
          name="font-family"
          options={Object.values(fontTypesMap).map((fontType) => {
            return {
              label: fontType.mapboxValue,
              value: fontType.id,
            };
          })}
          placeholder="Select Font Family"
          value={activeFeature?.properties?.['font-type']}
          onChange={(e) => {
            handleValueChange({ 'font-type': e });
          }}
        />
      </div>

      <ColorSwatches
        active={activeBorderColorConfig}
        label="Background Color"
        swatchConfig={customizeSwatchConfig}
        onClick={(e) => handleValueChange({ 'border-color': e.color })}
      />
      <div className="flex flex-col gap-2">
        <Typography className="text-gray-70" variant="body2">
          Background Opacity
        </Typography>
        <SingleSlider
          defaultValue={round(activeBorderOpacityConfig * 100)}
          handleChange={(val) =>
            handleValueChange({ 'border-opacity': val / 100 })
          }
        />
      </div>
    </>
  );
};

export default TextSettings;
